import type { ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import PortfolioSectionHeader from "@/v2-backoffice-client/portfolio/PortfolioSectionHeader"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import {
  CHURN_RISK_LABELS,
  type ChurnRiskLevel,
} from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import {
  CHURN_FILTER_LEVELS,
  CHURN_LEVEL_CHART_COLORS,
} from "@/v2-backoffice-client/portfolio/portfolio.constants"
import type { PortfolioRiskCounts } from "@/v2-backoffice-client/portfolio/portfolio.health.utils"

export type PortfolioRiskBreakdownProps = ComponentProps<"section"> & {
  counts: PortfolioRiskCounts
  /** Display currency for the per-level billed sums (NOK-only today). */
  currency?: string | null
  /** Levels selected in the GLOBAL churn filter; empty = no filter. */
  activeLevels: ChurnRiskLevel[]
  onLevelToggle(level: ChurnRiskLevel): void
}

// Customers by churn-risk level: a single composition bar (lengths compare —
// donut angles don't) over one row per level with the count and the
// 12-month billed revenue riding on it, so the risk always comes priced:
// "5 high risk" is a mood, "5 high risk · 1,2M NOK" is a priority. Every
// row toggles that level in the GLOBAL churn filter, so "high risk" scopes
// every tab (playbook included) to those accounts. Standard facet behavior:
// the OTHER filters narrow these counts, the churn selection itself only
// dims deselected levels so they stay clickable.
function PortfolioRiskBreakdown(props: PortfolioRiskBreakdownProps) {
  const {
    counts,
    currency,
    activeLevels,
    onLevelToggle,
    className,
    ...sectionProps
  } = props

  const hasSelection = activeLevels.length > 0
  const isExcluded = (level: ChurnRiskLevel) =>
    hasSelection && !activeLevels.includes(level)

  // Bar geometry uses ALL levels so segments hold their place when a level
  // is deselected (it dims instead of reflowing the bar).
  const totalCount = CHURN_FILTER_LEVELS.reduce(
    (sum, level) => sum + counts[level].count,
    0,
  )
  const scopedCount = CHURN_FILTER_LEVELS.reduce(
    (sum, level) => sum + (isExcluded(level) ? 0 : counts[level].count),
    0,
  )
  const scopedBilled = CHURN_FILTER_LEVELS.reduce(
    (sum, level) => sum + (isExcluded(level) ? 0 : counts[level].billed12M),
    0,
  )

  return (
    <section className={cn("flex flex-col gap-4", className)} {...sectionProps}>
      <PortfolioSectionHeader
        eyebrow="Risk"
        title="Churn risk"
        lead="How the customers split by churn read, and what each level billed over the last twelve months. Click a level to scope every tab to those accounts."
        info="Same rule-based read as the customer panels (last 28 days) — hover a badge there for the reasons. Clicking a level toggles it in the global churn filter, scoping every tab — including the playbook — to those accounts. Deselected levels dim instead of disappearing so they stay one click away. The billed column is what those customers paid over the last 12 months — the money the level puts at stake."
      >
        <span className="ml-auto text-xs tabular-nums text-muted-foreground">
          {formatNumber(scopedCount)} customers
          {scopedBilled > 0 && (
            <> · {formatCompactAmount(scopedBilled, currency)}</>
          )}
        </span>
      </PortfolioSectionHeader>

      <UsageReportCard className="flex-1">
        <div
          className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted"
          role="img"
          aria-label="Share of customers per churn-risk level"
        >
          {CHURN_FILTER_LEVELS.filter((level) => counts[level].count > 0).map(
            (level) => (
              <span
                key={level}
                className="h-full transition-opacity"
                style={{
                  width: `${(counts[level].count / Math.max(1, totalCount)) * 100}%`,
                  backgroundColor: CHURN_LEVEL_CHART_COLORS[level],
                  opacity: isExcluded(level) ? 0.25 : 1,
                }}
                title={`${CHURN_RISK_LABELS[level]}: ${formatNumber(counts[level].count)} customers`}
              />
            ),
          )}
        </div>

        <ul className="flex flex-col gap-1">
          {CHURN_FILTER_LEVELS.map((level) => {
            const entry = counts[level]
            const share =
              totalCount > 0 ? Math.round((entry.count / totalCount) * 100) : 0
            return (
              <li key={level}>
                <button
                  type="button"
                  aria-pressed={activeLevels.includes(level)}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-md border border-transparent px-2 py-2 text-left transition-colors hover:bg-muted/50",
                    activeLevels.includes(level) && "border-border bg-muted/50",
                    isExcluded(level) && "opacity-40",
                  )}
                  onClick={() => onLevelToggle(level)}
                >
                  <span
                    className="size-2 shrink-0 rounded-full"
                    style={{
                      backgroundColor: CHURN_LEVEL_CHART_COLORS[level],
                    }}
                  />
                  <span className="min-w-0 flex-1 truncate text-xs">
                    {CHURN_RISK_LABELS[level]}
                  </span>
                  <span className="shrink-0 text-sm font-semibold tabular-nums">
                    {formatNumber(entry.count)}
                    <span className="ml-1 text-xs font-normal text-muted-foreground">
                      · {share}%
                    </span>
                  </span>
                  <span
                    className="w-20 shrink-0 text-right text-xs tabular-nums text-muted-foreground"
                    title="Billed the last 12 months by these customers — the money their churn level puts at stake"
                  >
                    {entry.billed12M > 0
                      ? formatCompactAmount(entry.billed12M, currency)
                      : "—"}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </UsageReportCard>
    </section>
  )
}

export default PortfolioRiskBreakdown
